import React, { useEffect, useRef, useState } from "react";

export interface ConfirmModalProps {
  title: string;
  body: string;
  icon: string;                     // Material Symbols name (e.g., 'admin_panel_settings', 'person_remove', 'mail_off')
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/**
 * ConfirmModal — generic confirmation overlay reused by the role-change,
 * deactivate, and cancel-invite flows.
 *
 * Variants:
 *   - destructive=false (default): role-change. Icon color text-tertiary,
 *     panel border border-outline-variant/30, confirm button bg-primary.
 *   - destructive=true: deactivate / cancel-invite. Icon + title text-error,
 *     panel border border-error/20, confirm button bg-error-container.
 *
 * Interaction:
 *   - Backdrop click closes (unless submission in-flight)
 *   - Escape key closes (unless submission in-flight)
 *   - While submitting: backdrop click + Escape disabled; confirm button shows hourglass_empty
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  body,
  icon,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);

  // Caller's onConfirm typically calls setConfirmModal(null) on success, which
  // unmounts this very component before our finally block runs. Without this
  // guard, setSubmitting(false) fires on an unmounted component (silent no-op
  // in React 18 but flagged by StrictMode/lint). The ref-based check sidesteps
  // the warning entirely.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, submitting]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  const panelBorder = destructive ? "border-error/20" : "border-outline-variant/30";
  const iconColor = destructive ? "text-error" : "text-tertiary";
  const titleColor = destructive ? "text-error" : "text-on-surface";
  const confirmButtonClasses = destructive
    ? "px-4 py-2 bg-error-container text-error font-bold text-xs rounded-md uppercase tracking-tighter font-label border border-error/30 hover:bg-error/10 transition-all active:opacity-80 disabled:opacity-50"
    : "px-4 py-2 bg-primary text-on-primary font-bold text-xs rounded-md uppercase tracking-tighter font-label transition-all active:opacity-80 disabled:opacity-50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/60"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div className={`relative bg-surface-container rounded-lg border ${panelBorder} shadow-2xl w-full max-w-sm mx-4 p-6 animate-[slideDown_150ms_ease-out]`}>
        <div className="flex justify-center mb-4">
          <span className={`material-symbols-outlined text-4xl ${iconColor}`}>{icon}</span>
        </div>
        <h3 className={`text-base font-headline font-bold uppercase tracking-wide text-center mb-3 ${titleColor}`}>
          {title}
        </h3>
        <p className="text-sm font-body text-on-surface-variant text-center mb-6">
          {body}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-xs font-label font-bold uppercase tracking-tighter text-slate-400 hover:text-on-surface transition-colors rounded-md hover:bg-surface-container-high disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className={confirmButtonClasses}
          >
            {submitting ? (
              <span className="material-symbols-outlined text-base">hourglass_empty</span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
